import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCampaignAttribution } from '../../hooks/useCampaignAttribution';
import useSEO from '../../hooks/useSEO';
import {
  Offer, OfferItem, OfferPlan, OFFER_MEALS, formatDateRange, offersApi, scheduleDays, scheduleTotal, titleCase, formatPrice } from '../../services/offersApi';
import { OfferShell, OfferState, loadDraft } from './OfferShell';

export interface OfferData {
  offer: Offer;
  menu: OfferItem[];
  plans: OfferPlan[];
}

// Shared loader for the offer, its menu and its plans.
export function useOfferData(slug: string | undefined) {
  const [data, setData] = useState<OfferData | null>(null);
  const [error, setError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    (async () => {
      try {
        const offer = await offersApi.getBySlug(slug);
        const [menu, plans] = await Promise.all([offersApi.getMenu(offer._id), offersApi.getPlans(offer._id)]);
        if (!cancelled) setData({ offer, menu, plans });
      } catch (e) {
        const err = e as Error & { status?: number };
        if (!cancelled) setError({ message: err.message, notFound: err.status === 404 });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, attempt]);

  return { data, error, retry: () => setAttempt((n) => n + 1) };
}

const planHighlights = (plan: OfferPlan, menu: Map<string, OfferItem>): string[] => {
  const days = scheduleDays(plan.schedule);
  const meals = OFFER_MEALS.filter((meal) => days.some((day) => (plan.schedule[day]?.[meal] || []).length));
  const mealNames = new Set<string>();
  days.forEach((day) => OFFER_MEALS.forEach((meal) => (plan.schedule[day]?.[meal] || []).forEach((slot) => {
    const item = menu.get(slot.itemId);
    if (item && (item.type === 'breakfast' || item.type === 'lunch' || item.type === 'dinner')) mealNames.add(item.name);
  })));
  const list = [
    `${days.length} day${days.length === 1 ? '' : 's'}: ${days.map((d) => titleCase(d.slice(0, 3))).join(', ')}`,
    `${meals.map(titleCase).join(', ')} each day`,
  ];
  if (mealNames.size) list.push(`Includes ${Array.from(mealNames).slice(0, 3).join(', ')}${mealNames.size > 3 ? ' and more' : ''}`);
  list.push('Swap meals and add drinks or extras');
  return list;
};

export default function OfferPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, error, retry } = useOfferData(slug);
  useCampaignAttribution(slug); // remember ?ref= / UTM codes for checkout
  const menu = useMemo(() => new Map((data?.menu || []).map((item) => [item._id, item])), [data]);

  useSEO({
    title: data ? `${data.offer.name} - BOQBOX meals` : 'Special meal offer - BOQBOX',
    description: data?.offer.description || 'Choose a meal plan and we’ll handle the rest.',
    ogImage: data?.offer.image || undefined,
  });

  if (error) {
    return (
      <OfferShell>
        <OfferState
          icon={error.notFound ? '🔎' : '⚠️'}
          title={error.notFound ? 'This offer isn’t available' : 'We couldn’t load this offer'}
          message={error.notFound ? 'It may have ended or the link may be wrong.' : error.message}
          action={error.notFound ? <Link className="mo-btn mo-btn-primary" to="/offers">See all offers</Link> : <button className="mo-btn mo-btn-primary" onClick={retry}>Try again</button>}
        />
      </OfferShell>
    );
  }

  if (!data) {
    return (
      <OfferShell>
        <div className="mo-skeleton" style={{ height: 320, borderRadius: 0 }} />
        <div className="mo-container mo-section mo-grid mo-grid-3">
          {[0, 1, 2].map((i) => <div key={i} className="mo-skeleton" style={{ height: 280 }} />)}
        </div>
      </OfferShell>
    );
  }

  const { offer, plans } = data;
  const soldOut = Boolean(offer.totalSlots) && (offer.remainingSlots ?? 0) <= 0;
  const dates = formatDateRange(offer.startDate, offer.endDate);
  const draft = loadDraft(offer.slug);
  const fewLeft = offer.totalSlots && (offer.remainingSlots ?? 0) > 0 && (offer.remainingSlots ?? 0) <= Math.max(10, offer.totalSlots * 0.1);
  const pickup = offer.pickupEnabled !== false;

  return (
    <OfferShell>
      <section className="mo-hero">
        {offer.image ? <img className="mo-hero-img" src={offer.image} alt="" /> : null}
        <div className="mo-container">
          <div className="mo-chips">
            {dates ? <span className="mo-chip">📅 {dates}</span> : null}
            {soldOut ? <span className="mo-chip">Sold out</span> : fewLeft ? <span className="mo-chip">🔥 Only {offer.remainingSlots} slots left</span> : null}
          </div>
          <h1>{offer.name}</h1>
          {offer.description ? <p>{offer.description}</p> : null}
        </div>
      </section>

      <div className="mo-container mo-section">
        {draft?.planId && plans.some((p) => p._id === draft.planId) && !soldOut ? (
          <div className="mo-card mo-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <strong>You have a reservation in progress</strong>
              <div style={{ color: 'var(--mo-ink-2)', fontSize: 14 }}>Pick up where you left off.</div>
            </div>
            <button className="mo-btn mo-btn-primary mo-btn-sm" onClick={() => navigate(`/offers/${encodeURIComponent(offer.slug)}/order?plan=${draft.planId}`)}>Continue</button>
          </div>
        ) : null}

        <div className="mo-section-head">
          <div>
            <h2>Choose a plan</h2>
            <p>Every plan can be customised — swap meals, change quantities and add extras.</p>
          </div>
        </div>

        {plans.length === 0 ? (
          <OfferState icon="🍽️" title="Plans are coming soon" message="Meal plans for this offer haven’t been published yet." />
        ) : (
          <div className="mo-grid mo-grid-3">
            {plans.map((plan) => {
              const current = scheduleTotal(plan.schedule, menu) || plan.cost;
              return (
                <div key={plan._id} className={`mo-card mo-plan${plan.is_popular ? ' mo-plan-popular' : ''}`}>
                  {plan.is_popular ? <span className="mo-plan-badge">Most popular</span> : null}
                  <h3>{plan.name}</h3>
                  <div className="mo-plan-price">{formatPrice(current)} <small>from</small></div>
                  {plan.description ? <p>{plan.description}</p> : null}
                  <ul>{planHighlights(plan, menu).map((line) => <li key={line}>{line}</li>)}</ul>
                  {plan.additional_info ? <p><strong>Best for:</strong> {plan.additional_info}</p> : null}
                  {plan.caveats ? <p style={{ fontSize: 13, color: 'var(--mo-ink-3)' }}>{plan.caveats}</p> : null}
                  <button className={`mo-btn mo-btn-block ${plan.is_popular ? 'mo-btn-primary' : 'mo-btn-ghost'}`} disabled={soldOut} onClick={() => navigate(`/offers/${encodeURIComponent(offer.slug)}/order?plan=${plan._id}`)}>
                    {soldOut ? 'Sold out' : `Choose ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mo-info">
          <div className="mo-card">
            <span className="mo-info-icon" aria-hidden="true">🛵</span>
            <div><strong>Delivered to you</strong><span>{formatPrice(offer.deliveryFeePerMeal ?? 400)} per delivered meal, straight to your address.</span></div>
          </div>
          <div className="mo-card">
            <span className="mo-info-icon" aria-hidden="true">🏪</span>
            <div>
              <strong>{pickup ? 'Or pick up for free' : 'Delivery only'}</strong>
              <span>{pickup ? (offer.pickupInstruction || `Pickup is available from ${titleCase(offer.standPickupAvailableFrom || 'thursday')}.`) : 'All meals for this offer are delivered.'}</span>
            </div>
          </div>
          <div className="mo-card">
            <span className="mo-info-icon" aria-hidden="true">🔒</span>
            <div><strong>Secure payment</strong><span>Pay safely with card, bank transfer or USSD via Paystack.{offer.supportPhone ? ` Questions? Call ${offer.supportPhone}.` : ''}</span></div>
          </div>
        </div>
      </div>
    </OfferShell>
  );
}
